/**
 * Frontend error reporter.
 *
 * Always logs to console so a production crash leaves a trace in the user's
 * DevTools (LR-104 review L-1 made this unconditional). Additionally fires a
 * non-blocking `navigator.sendBeacon` to `VITE_ERROR_SINK_URL` when that env
 * var is set — by default it is empty, so nothing is shipped off-device. The
 * sink itself (real Sentry / proxy endpoint / etc.) is intentionally out of
 * scope for LR-421: this PR wires the switch-point, not the backend.
 */

export type ErrorSource =
  | 'react-boundary'
  | 'window-error'
  | 'unhandled-rejection';

export interface ErrorContext {
  source: ErrorSource;
  componentStack?: string;
}

interface ErrorPayload {
  message: string;
  stack?: string;
  componentStack?: string;
  source: ErrorSource;
  url: string;
  ts: string;
}

function toError(value: unknown): Error {
  if (value instanceof Error) return value;
  if (typeof value === 'string') return new Error(value);
  try {
    return new Error(JSON.stringify(value));
  } catch {
    return new Error('Unknown error (non-serializable)');
  }
}

function buildPayload(error: Error, context: ErrorContext): ErrorPayload {
  return {
    message: error.message,
    stack: error.stack,
    componentStack: context.componentStack,
    source: context.source,
    url: typeof window !== 'undefined' ? window.location.href : '',
    ts: new Date().toISOString(),
  };
}

function sendToSink(payload: ErrorPayload): void {
  const sinkUrl = import.meta.env.VITE_ERROR_SINK_URL;
  if (typeof sinkUrl !== 'string' || !sinkUrl) return;
  if (
    typeof navigator === 'undefined' ||
    typeof navigator.sendBeacon !== 'function'
  ) {
    return;
  }
  try {
    const blob = new Blob([JSON.stringify(payload)], {
      type: 'application/json',
    });
    navigator.sendBeacon(sinkUrl, blob);
  } catch {
    // sendBeacon may throw on quota / cross-origin / disabled APIs.
    // Swallow so we don't loop back into our own error handler.
  }
}

/**
 * Report a runtime error. Always logs to console; conditionally beacons to
 * the configured sink URL.
 */
export function reportError(value: unknown, context: ErrorContext): void {
  const error = toError(value);
  // Unconditional console — production crashes otherwise leave no trace at all
  // since the React error boundary swallows them (LR-104 review L-1).
  console.error(`[${context.source}]`, error, context.componentStack ?? '');
  sendToSink(buildPayload(error, context));
}

let handlersInstalled = false;

/**
 * Install global handlers for uncaught errors and unhandled promise
 * rejections. Idempotent — safe to call multiple times (e.g. HMR).
 */
export function installGlobalErrorHandlers(): void {
  if (handlersInstalled) return;
  if (typeof window === 'undefined') return;
  handlersInstalled = true;
  window.addEventListener('error', (event) => {
    reportError(event.error ?? event.message, { source: 'window-error' });
  });
  window.addEventListener('unhandledrejection', (event) => {
    reportError(event.reason, { source: 'unhandled-rejection' });
  });
}

/** Test-only: reset the install-once guard. */
export function __resetErrorHandlersForTests(): void {
  handlersInstalled = false;
}
